import { X } from 'lucide-react';
import { useMemo } from 'react';

import type { ContactDto } from '../../api/types';
import { ContactPanel } from '../dialogs/ContactPanel';
import { DialogConversation } from '../dialogs/DialogConversation';
import { useDialog } from '../../hooks/use-dialogs';
import { useContact } from '../../hooks/use-contacts';
import { Skeleton } from '../ui/skeleton';

function contactName(c: ContactDto): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ');
  return name || `VK ID ${c.vkUserId}`;
}

/**
 * Side panel opened by a kanban card click (§U4 item 2). Reuses the same contact card +
 * conversation as the inbox: it finds the contact's most recently updated active dialog
 * (GET /contacts/:id → activeDialogs) and hands it to DialogConversation/ContactPanel.
 */
export function KanbanContactPanel({ contact, onClose }: { contact: ContactDto; onClose: () => void }) {
  const detailQuery = useContact(contact.id);

  const activeDialogId = useMemo(() => {
    const dialogs = detailQuery.data?.activeDialogs ?? [];
    if (dialogs.length === 0) return undefined;
    return [...dialogs].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]?.id;
  }, [detailQuery.data]);

  const dialogQuery = useDialog(activeDialogId);

  return (
    // Below `lg` (1024px) this overlays the board instead of pushing it — a fixed 720px
    // panel next to a horizontally-scrolling board used to leave the board a sliver wide.
    <>
      <div aria-hidden="true" onClick={onClose} className="fixed inset-0 z-30 bg-black/30 lg:hidden" />
      <div className="fixed inset-y-0 right-0 z-40 flex w-full border-l border-border bg-card shadow-xl sm:w-[420px] lg:static lg:z-auto lg:h-full lg:w-[560px] lg:shrink-0 lg:shadow-none xl:w-[720px]">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="font-semibold">{contactName(contact)}</h2>
            <button
              type="button"
              aria-label="Закрыть панель"
              onClick={onClose}
              className="rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {detailQuery.isLoading && (
            <div className="flex flex-col gap-2 p-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-full w-full" />
            </div>
          )}
          {!detailQuery.isLoading && !activeDialogId && (
            <p className="p-4 text-sm text-muted-fg">Нет активных диалогов у этого контакта.</p>
          )}
          {dialogQuery.data && <DialogConversation dialog={dialogQuery.data} showOpenFullLink />}
        </div>
        {dialogQuery.data && <ContactPanel dialog={dialogQuery.data} />}
      </div>
    </>
  );
}
